import { type TFunction } from 'i18next'
import * as pathToRegexp from 'path-to-regexp'
import React from 'react'
import { matchPath, useLocation, useParams } from 'react-router'

import { type TranslationSchema } from '~/locales/resources'

type N = Record<string, string>
type M = Record<string, string>

/**
 * パスを解決するための関数を作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @param pathRecord - パスのレコード。
 * @param prefix - パスに付与するプレフィックス。
 * @returns パスを解決するための関数。
 */
export function createPathResolver<TPathRecord extends N>(
  pathRecord: TPathRecord,
  prefix = '',
) {
  return <TKey extends keyof TPathRecord>(key: TKey) =>
    `${prefix}${pathRecord[key]}`
}

/**
 * パスをコンパイルするための関数を作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @param pathRecord - パスのレコード。
 * @param prefix - パスに付与するプレフィックス。
 * @returns パスをコンパイルするための関数。
 */
export function createPathCompiler<TPathRecord extends N>(
  pathRecord: TPathRecord,
  prefix = '',
) {
  return <TKey extends keyof TPathRecord>(
    key: TKey,
    params: AnalyzePathParams<TPathRecord[TKey]>,
  ) => pathToRegexp.compile(createPathResolver(pathRecord, prefix)(key))(params)
}

/**
 * マッチしたパスのキーを返すカスタムフックを作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @param pathRecord - パスのレコード。
 * @param prefix - パスに付与するプレフィックス。
 * @returns マッチしたパスのキーを返すカスタムフック。
 */
export function createMatchedPathKeyHook<TPathRecord extends N>(
  pathRecord: TPathRecord,
  prefix = '',
) {
  const useMatchedPathKey = <TKey extends keyof TPathRecord>(keys: TKey[]) => {
    const location = useLocation()

    return React.useMemo(
      () =>
        keys.find(
          (key) =>
            matchPath(
              {
                path: createPathResolver(pathRecord, prefix)(key),
              },
              location.pathname,
            ) !== null,
        ),
      [keys, location.pathname],
    )
  }

  return useMatchedPathKey
}

/**
 * パスのパラメータを返すカスタムフックを作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @param _pathRecord - パスのレコード。
 * @returns パスのパラメータを返すカスタムフック。
 */
export function createPathParamsHook<TPathRecord extends N>(
  _pathRecord: TPathRecord,
) {
  const usePathParams = <TKey extends keyof TPathRecord>(_key: TKey) =>
    useParams<AnalyzePathParams<TPathRecord[TKey]>>()

  return usePathParams
}

export type LabeledPath = {
  label: string
  to: string
}

/**
 * ラベル付きのパスを作成するための関数を作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @param pathRecord - パスのレコード。
 * @param pathLabelI18nKeyMap - パスのラベルのマップ。
 * @param t - i18next の翻訳関数。
 * @param prefix - パスに付与するプレフィックス。
 * @returns ラベル付きのパスを作成するための関数。
 *
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export function createLabeledPathCreatorWithI18n<TPathRecord extends N>(
  pathRecord: TPathRecord,
  pathLabelI18nKeyMap: Record<keyof TPathRecord, keyof TranslationSchema>,
  t: TFunction,
  prefix = '',
) {
  return <TKey extends keyof TPathRecord>(
    key: TKey,
    params: AnalyzePathParams<TPathRecord[TKey]>,
  ): LabeledPath => ({
    label: t(pathLabelI18nKeyMap[key]),
    to: createPathCompiler(pathRecord, prefix)(key, params),
  })
}

export type Breadcrumb = {
  disabled?: boolean
  label: string
  to?: string
}

/**
 * @deprecated Use pathGroupLabelI18nKeyMap with t() function instead
 */
/**
 *  パンくずを作成するための関数を作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @template TPathGroupLabelMap - パスグループのラベルのマップの型。
 * @param pathRecord - パスのレコード。
 * @param pathLabelMap - パスのラベルのマップ。
 * @param pathGroupLabelMap - パスグループのラベルのマップ。
 * @param prefix - パスに付与するプレフィックス。
 * @returns パンくずを作成するための関数。
 */
export function createBreadcrumbCreator<
  TPathRecord extends N,
  TPathGroupLabelMap extends M,
>(
  pathRecord: TPathRecord,
  pathLabelMap: Record<keyof TPathRecord, string>,
  pathGroupLabelMap: TPathGroupLabelMap,
  prefix = '',
) {
  type PathArgs<TKey extends keyof TPathRecord> =
    | [TKey, AnalyzePathParams<TPathRecord[TKey]>, string]
    | [TKey, AnalyzePathParams<TPathRecord[TKey]>]
  type PathGroupArgs<TPathGroupKey extends keyof TPathGroupLabelMap> = [
    TPathGroupKey,
  ]
  type CreateBreadcrumbArgs<
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  > = PathArgs<TKey> | PathGroupArgs<TPathGroupKey>

  const isPathArgs = <
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  >(
    args: CreateBreadcrumbArgs<TKey, TPathGroupKey>,
  ): args is PathArgs<TKey> => args[1] !== undefined

  return <
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  >(
    ...args: CreateBreadcrumbArgs<TKey, TPathGroupKey>
  ): Breadcrumb => {
    if (isPathArgs(args)) {
      const [key, params, label] = args

      return {
        label: label ?? pathLabelMap[key],
        to: createPathCompiler(pathRecord, prefix)(key, params),
      }
    }

    const [pathGroupKey] = args

    return {
      disabled: true,
      label: pathGroupLabelMap[pathGroupKey],
    }
  }
}

/**
 *  パンくずを作成するための関数を作成します。
 *
 * @template TPathRecord - パスのレコードの型。
 * @template TPathGroupLabelMap - パスグループのラベルのマップの型。
 * @param pathRecord - パスのレコード。
 * @param pathLabelMap - パスのラベルのマップ。
 * @param pathGroupLabelMap - パスグループのラベルのマップ。
 * @param t - i18next の翻訳関数。
 * @param prefix - パスに付与するプレフィックス。
 * @returns パンくずを作成するための関数。
 *
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export function createBreadcrumbCreatorWithI18n<
  TPathRecord extends N,
  TPathGroupLabelMap extends M,
>(
  pathRecord: TPathRecord,
  pathLabelMap: Record<keyof TPathRecord, keyof TranslationSchema>,
  pathGroupLabelMap: Record<keyof TPathGroupLabelMap, keyof TranslationSchema>,
  t: TFunction,
  prefix = '',
) {
  type PathArgs<TKey extends keyof TPathRecord> =
    | [TKey, AnalyzePathParams<TPathRecord[TKey]>, string]
    | [TKey, AnalyzePathParams<TPathRecord[TKey]>]
  type PathGroupArgs<TPathGroupKey extends keyof TPathGroupLabelMap> = [
    TPathGroupKey,
  ]
  type CreateBreadcrumbArgs<
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  > = PathArgs<TKey> | PathGroupArgs<TPathGroupKey>

  const isPathArgs = <
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  >(
    args: CreateBreadcrumbArgs<TKey, TPathGroupKey>,
  ): args is PathArgs<TKey> => args[1] !== undefined

  return <
    TKey extends keyof TPathRecord,
    TPathGroupKey extends keyof TPathGroupLabelMap,
  >(
    ...args: CreateBreadcrumbArgs<TKey, TPathGroupKey>
  ): Breadcrumb => {
    if (isPathArgs(args)) {
      const [key, params, label] = args

      return {
        label: label ?? t(pathLabelMap[key]),
        to: createPathCompiler(pathRecord, prefix)(key, params),
      }
    }

    const [pathGroupKey] = args

    return {
      disabled: true,
      label: t(pathGroupLabelMap[pathGroupKey]),
    }
  }
}
